/**
 * Intake opens the story after two answers. Submitting highlights
 * opens a written discussion. The server advances after two learner responses;
 * the learner may also use Continue where available.
 * Earlier persisted beat names and routes remain readable on resume.
 */

import type { DojoBeat, LegacyDojoBeat } from '@/types/dojo';

export const VALID_DOJO_BEAT_TRANSITIONS: Record<DojoBeat, DojoBeat[]> = {
  idle: ['intake'],
  intake: ['highlight', 'react_well', 'abandoned'],
  highlight: ['react_well', 'enact', 'abandoned'],
  react_well: ['react_better', 'abandoned'],
  react_better: ['enact', 'highlight', 'abandoned'],
  enact: ['chiddush', 'abandoned'],
  chiddush: ['completed', 'abandoned'],
  completed: [],
  abandoned: [],
};

// The only transitions the client is allowed to request itself.
export const CLIENT_DRIVEN_BEAT_TRANSITIONS: Array<[DojoBeat, DojoBeat]> = [
  ['highlight', 'react_well'],
  ['chiddush', 'completed'],
  ['react_well', 'react_better'],
  ['react_better', 'enact'],
  ['enact', 'chiddush'],
];

// Rounds created before the unit flow still emit the old vocabulary. Map each
// onto the nearest unit beat so an in-flight legacy round degrades instead of
// stalling on an ignored transition.
const LEGACY_BEAT_ALIASES: Record<LegacyDojoBeat, DojoBeat> = {
  ceremony: 'intake',
  react: 'react_well',
  unpack: 'react_better',
  reconsider: 'highlight',
  apply: 'enact',
};

const UNIT_BEATS = new Set<string>(Object.keys(VALID_DOJO_BEAT_TRANSITIONS));

/** Coerce a wire beat value (new or legacy) to a unit beat, or null if unknown. */
export function normalizeDojoBeat(value: unknown): DojoBeat | null {
  if (typeof value !== 'string') return null;
  if (UNIT_BEATS.has(value)) return value as DojoBeat;
  return LEGACY_BEAT_ALIASES[value as LegacyDojoBeat] ?? null;
}

export function isValidBeatTransition(fromBeat: DojoBeat, toBeat: DojoBeat): boolean {
  return fromBeat === toBeat || VALID_DOJO_BEAT_TRANSITIONS[fromBeat]?.includes(toBeat) === true;
}

export function isClientDrivenTransition(fromBeat: DojoBeat, toBeat: DojoBeat): boolean {
  return CLIENT_DRIVEN_BEAT_TRANSITIONS.some(([from, to]) => from === fromBeat && to === toBeat);
}
